/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Bars3Icon, XMarkIcon, GlobeAltIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import DesignTokens from '../../styles/DesignTokens';
import SafarGlimpseLogo from './SafarGlimpseLogo';
import UserMenu from '../auth/UserMenu';
import LoginButton from '../auth/LoginButton';
import { useAuth } from '../../contexts/AuthContext';
import CreateTripSheet from '../shared/CreateTripSheet';

const NavbarContainer = styled.nav`
  position: ${props => props.isScrolled ? 'fixed' : 'absolute'};
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${props => props.isScrolled
    ? '#FFFFFF'
    : 'rgba(0, 8, 22, 0.60)'};
  backdrop-filter: ${props => props.isScrolled ? 'none' : 'blur(16px)'};
  -webkit-backdrop-filter: ${props => props.isScrolled ? 'none' : 'blur(16px)'};
  border-bottom: ${props => props.isScrolled
    ? '1px solid rgba(0, 0, 0, 0.1)'
    : '1px solid rgba(255, 255, 255, 0.08)'};
  padding: ${props => props.isScrolled ? '0.5rem 0' : '0.75rem 0'};
  box-shadow: ${props => props.isScrolled
    ? '0 2px 16px rgba(0, 0, 0, 0.08)'
    : 'none'};
  display: flex;
  align-items: center;
  overflow: visible;
  transition: background 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              padding 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  /* Hide on mobile — BottomNavigation handles mobile nav */
  @media (max-width: ${props => props.theme.breakpoints?.tablet || '768px'}) {
    display: none;
  }
`;

const NavContent = styled.div`
  max-width: ${props => props.theme.container?.xl || '1200px'};
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing?.md || '1rem'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  width: 100%;
  height: 100%;
  
  > * {
    display: flex;
    align-items: center;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  flex-shrink: 0;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  /* Override global link styles */
  color: inherit !important;
  background-color: transparent !important;
  padding: 0 !important;
  margin: 0 !important;
  border-radius: 0 !important;
  transition: none !important;
  
  &:hover {
    opacity: 1 !important;
    transform: none !important;
    color: inherit !important;
    background-color: transparent !important;
  }
  
  &:active {
    opacity: 1 !important;
    transform: none !important;
    color: inherit !important;
    background-color: transparent !important;
  }
  
  &:focus {
    background-color: transparent !important;
    outline: none;
  }
`;

const LogoImage = styled.img`
  height: ${props => props.isScrolled ? '88px' : '88px'};
  width: auto;
  object-fit: contain;
  display: block;
  pointer-events: none;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  /* Allow logo to extend beyond navbar padding using negative margins */
  margin: ${props => props.isScrolled ? '-10px 0' : '-13px 0'};

  @media (max-width: ${props => props.theme.breakpoints?.tablet || '768px'}) {
    height: ${props => props.isScrolled ? '88px' : '100px'};
    margin: ${props => props.isScrolled ? '-9px 0' : '-12px 0'};
  }
  
  @media (max-width: ${props => props.theme.breakpoints?.mobile || '480px'}) {
    height: 80px;
    margin: -9px 0;
  }
`;

const DesktopMenu = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  height: 100%;

  @media (max-width: ${props => props.theme.breakpoints?.tablet || '768px'}) {
    display: none;
  }
`;

const NavLinksGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const AuthGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const NavLink = styled(Link)`
  font-weight: 500;
  font-size: ${props => props.theme.fontSizes?.sm || '0.9375rem'};
  color: ${props => props.isScrolled ? props.theme.colors.text : 'white'};
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;
  white-space: nowrap;
  background: ${props => props.isScrolled 
    ? 'transparent' 
    : 'rgba(255, 255, 255, 0.15)'};
  backdrop-filter: ${props => props.isScrolled ? 'none' : 'blur(10px)'};
  border: ${props => props.isScrolled 
    ? 'none' 
    : '1px solid rgba(255, 255, 255, 0.2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  line-height: 1.4;

  &:hover {
    color: ${props => props.isScrolled ? props.theme.colors.primary : 'white'};
    background: ${props => props.isScrolled 
      ? 'rgba(0, 0, 0, 0.05)' 
      : 'rgba(255, 255, 255, 0.25)'};
    border-color: ${props => props.isScrolled 
      ? 'transparent' 
      : 'rgba(255, 255, 255, 0.3)'};
    transform: translateY(-1px);
  }

  &.active {
    color: ${props => props.isScrolled ? props.theme.colors.primary : 'white'};
    background: ${props => props.isScrolled 
      ? 'rgba(66, 133, 244, 0.1)' 
      : 'rgba(255, 255, 255, 0.3)'};
    border-color: ${props => props.isScrolled 
      ? 'transparent' 
      : 'rgba(255, 255, 255, 0.4)'};
    font-weight: 600;
  }
`;

const CTAButton = styled.button`
  font-weight: 600;
  font-size: ${props => props.theme.fontSizes?.sm || '0.875rem'};
  color: white;
  text-decoration: none;
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  white-space: nowrap;
  background: ${props => props.theme.colors.primary || '#4285F4'};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  box-shadow: 0 2px 8px rgba(66, 133, 244, 0.25);
  cursor: pointer;

  &:hover {
    background: ${props => props.theme.colors.primaryHover || '#3367D6'};
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(66, 133, 244, 0.4);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  padding: 0.75rem;
  cursor: pointer;
  color: white;
  transition: all 0.2s ease;
  min-height: 44px;
  min-width: 44px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  svg {
    width: 24px;
    height: 24px;
    stroke-width: 2.5;
    color: white;
    display: block;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.28);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: ${props => props.theme.breakpoints?.tablet || '768px'}) {
    display: flex;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #FFFFFF;
  z-index: 1001;
  display: none;
  flex-direction: column;
  padding: 2rem;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: flex;
  }
`;

const MobileMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  position: relative;
`;

const MobileMenuLogo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  z-index: 1;
  
  img {
    height: 60px;
    width: auto;
    object-fit: contain;
  }
`;

const MobileMenuCloseButton = styled.button`
  background: #f7f7f7;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 0.75rem;
  cursor: pointer;
  color: #333;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  z-index: 1;
  
  &:hover {
    background: #ebebeb;
    border-color: #d0d0d0;
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const MobileMenuLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const MobileNavLink = styled(Link)`
  font-size: 1rem;
  font-weight: 600;
  color: white;
  text-decoration: none;
  padding: 1rem 1.25rem;
  transition: all 0.22s ease;
  background: linear-gradient(135deg, #4285F4 0%, #3367D6 100%);
  border-radius: 14px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 3px 10px rgba(66, 133, 244, 0.22);

  &:hover {
    color: white;
    background: linear-gradient(135deg, #5294F7 0%, #4278E8 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(66, 133, 244, 0.35);
  }

  &.active {
    color: white;
    background: linear-gradient(135deg, #1A73E8 0%, #1557B0 100%);
    box-shadow: 0 4px 14px rgba(26, 115, 232, 0.45);
  }

  svg {
    width: 18px;
    height: 18px;
    opacity: 0.75;
    flex-shrink: 0;
  }
`;

const MobileSearchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f0f5ff;
  border: 1.5px solid #c5d8fb;
  color: #4285F4;
  padding: 1rem 1.25rem;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.22s ease;
  width: 100%;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(66, 133, 244, 0.08);

  svg {
    width: 18px;
    height: 18px;
    opacity: 0.7;
    flex-shrink: 0;
  }

  &:hover {
    background: #e4eeff;
    border-color: #a8c4fa;
    transform: translateY(-2px);
    box-shadow: 0 5px 14px rgba(66, 133, 244, 0.18);
  }

  &:active {
    transform: translateY(0);
  }
`;

const TopSearchForm = styled.form`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.75rem;
  height: 100%;
  position: relative;

  input {
    height: 44px;
    padding: 0 12px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    font-size: ${DesignTokens.typography.fontSize.sm};
    width: 220px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    color: ${props => props.theme.colors.text || '#333'};
    
    &::placeholder {
      color: ${props => props.theme.colors.textSecondary || '#666'};
    }
  }

  button {
    height: 44px;
    padding: 0 16px;
    border: none;
    background: ${props => props.theme.colors.primary};
    color: white;
    border-radius: 8px;
    font-size: ${DesignTokens.typography.fontSize.sm};
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
      background: ${props => props.theme.colors.primaryHover || props.theme.colors.primary};
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { isAuthenticated } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);

  // Handle scroll for sticky navigation
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 100); // Activate after 100px scroll
    };

    window.addEventListener('scroll', handleScroll);
    // Check initial scroll position (for when page loads scrolled)
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hide navbar only on itinerary detail pages (with an ID), not the listing page
  const isItineraryDetailPage = /^\/itineraries\/.+/.test(location.pathname);
  if (isItineraryDetailPage) return null;

  const navigationItems = [
    { path: '/', label: 'Home' },
    { path: '/heritage', label: 'Heritage' },
    { path: '/itineraries', label: 'Recommendations' },
    { path: '/guides', label: 'Guides' },
    { path: '/about', label: 'About' }
  ];

  const isActiveLink = (path) => {
    if (path === '/') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <NavbarContainer isScrolled={isScrolled}>
        <NavContent>
          <DesktopMenu>
            <NavLinksGroup>
              {navigationItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={isActiveLink(item.path) ? 'active' : ''}
                  isScrolled={isScrolled}
                >
                  {item.label}
                </NavLink>
              ))}
            </NavLinksGroup>
            <AuthGroup>
              <CTAButton
                onClick={() => setSheetOpen(true)}
                isScrolled={isScrolled}
              >
                Start My Plan
              </CTAButton>
              {isAuthenticated ? <UserMenu /> : <LoginButton compact />}
            </AuthGroup>
          </DesktopMenu>

          <MobileMenuButton onClick={toggleMobileMenu} aria-label="Toggle menu">
            {isMobileMenuOpen ? (
              <XMarkIcon style={{ width: '24px', height: '24px', color: 'white' }} />
            ) : (
              <Bars3Icon style={{ width: '24px', height: '24px', color: 'white' }} />
            )}
          </MobileMenuButton>
        </NavContent>
      </NavbarContainer>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            <MobileMenuHeader>
              <MobileMenuLogo to="/" onClick={closeMobileMenu}>
                <SafarGlimpseLogo variant="light" height={40} />
              </MobileMenuLogo>
              <MobileMenuCloseButton onClick={toggleMobileMenu} aria-label="Close menu">
                <XMarkIcon />
              </MobileMenuCloseButton>
            </MobileMenuHeader>

            <MobileMenuLinks>
              {navigationItems.map((item) => (
                <MobileNavLink
                  key={item.path}
                  to={item.path}
                  className={isActiveLink(item.path) ? 'active' : ''}
                  onClick={closeMobileMenu}
                >
                  <span>{item.label}</span>
                  <ChevronRightIcon />
                </MobileNavLink>
              ))}
              <MobileSearchButton onClick={() => { navigate('/places'); closeMobileMenu(); }}>
                <span>Search Places</span>
                <ChevronRightIcon />
              </MobileSearchButton>
              <div style={{ padding: '16px 4px 4px', borderTop: '1px solid #edf2ff' }}>
                {isAuthenticated ? <UserMenu /> : <LoginButton fullWidth />}
              </div>
            </MobileMenuLinks>
          </MobileMenu>
        )}
      </AnimatePresence>
      <CreateTripSheet isOpen={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
}

export default Navbar;